import React, { useState, useEffect } from 'react';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// Email templates for different follow-up types
const emailTemplates = {
  intake: {
    subject: 'Following Up - Document Collection',
    body: `Hi {name},

I hope you're doing well! I wanted to follow up on collecting the intake documents we discussed. Having these documents ready will help us move forward quickly with your loan application.

Could you please let me know if you have any questions about what's needed, or if there's anything I can help with?

Looking forward to hearing from you.

Best regards,
Isak
Lended`
  },
  newDeal: {
    subject: 'Checking In - Any New Opportunities?',
    body: `Hi {name},

I hope all is well! I wanted to check in and see if you have any new deals or opportunities you're working on.

We're always here to help with financing for your next acquisition. Let me know if there's anything we can assist with.

Best regards,
Isak
Lended`
  },
  business: {
    subject: 'Following Up - Borrower Intake for {businessName}',
    body: `Hi {name},

I hope you're doing well! I wanted to follow up regarding {businessName}. We have the business prequalified, and now we need to collect the borrower intake documents to move forward.

Could you help us connect with the borrower, or let me know the best way to proceed?

Thanks so much!

Best regards,
Isak
Lended`
  },
  broker: {
    subject: 'Checking In',
    body: `Hi {name},

I hope you're doing well! It's been a while since we last connected, and I wanted to reach out to see how things are going.

Do you have any new deals or borrowers we might be able to help with? We're always looking to support your transactions with our financing solutions.

Looking forward to hearing from you!

Best regards,
Isak
Lended`
  },
  offMarket: {
    subject: 'Off-Market Deal Sourcing - Next Steps',
    body: `Hi {name},

Thank you for your interest in our off-market deal sourcing service. Here are the next steps to get started:

1. [Placeholder - User will provide specific steps]
2. [Placeholder]
3. [Placeholder]

Please let me know if you have any questions or if there's anything specific you're looking for.

Best regards,
Isak
Lended`
  },
  offMarketFollowUp: {
    subject: 'Following Up - Off-Market Opportunities',
    body: `Hi {name},

I wanted to follow up on our conversation about off-market opportunities. Have you had a chance to review the information I sent?

Please let me know if you have any questions or if there's anything I can help with.

Looking forward to hearing from you!

Best regards,
Isak
Lended`
  }
};

const FollowUpEmailModal = ({
  isOpen,
  onClose,
  recipientEmail,
  recipientName,
  templateType = 'intake',
  businessName = '',
  customSubject = '',
  customBody = '',
  onSuccess
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Use custom subject/body if provided (from follow-up sequence templates)
      if (customSubject || customBody) {
        setSubject(customSubject || '');
        setBody(customBody || '');
      } else if (templateType && emailTemplates[templateType]) {
        const template = emailTemplates[templateType];
        let processedSubject = template.subject
          .replace('{name}', recipientName || '')
          .replace('{businessName}', businessName || '');
        let processedBody = template.body
          .replace(/{name}/g, recipientName || '')
          .replace(/{businessName}/g, businessName || '');
        setSubject(processedSubject);
        setBody(processedBody);
      }
    }
  }, [isOpen, templateType, recipientName, businessName, customSubject, customBody]);

  const handleSend = async () => {
    if (!recipientEmail || recipientEmail.trim() === '') {
      alert('No recipient email provided');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      alert(`Invalid email address: ${recipientEmail}`);
      return;
    }

    setSending(true);
    try {
      const emailRef = collection(db, 'email');

      // Format the body with proper HTML paragraphs
      const htmlBody = body
        .split('\n\n')
        .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
        .join('');

      // Generate a unique ID for tracking replies
      const emailDocRef = doc(emailRef);
      const emailDocId = emailDocRef.id;

      const emailData = {
        to: recipientEmail,
        from: "Isak @ Lended <isak@lended.ai>",
        replyTo: `reply+${emailDocId}@parse.lended.ai`,
        subject: subject,
        templateName: "FollowUp",
        message: {
          subject: subject,
          firstName: recipientName,
          html: htmlBody
        },
        // Track reply status
        replied: false,
        repliedAt: null,
        sentAt: new Date().toISOString()
      };

      console.log('Sending email to:', recipientEmail, 'with data:', emailData);
      await setDoc(emailDocRef, emailData);
      console.log('Email document created with ID:', emailDocId);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
      alert('Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Send Follow-up Email</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* To field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <input
              type="email"
              value={recipientEmail || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
            />
          </div>

          {/* Subject field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Body field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !recipientEmail}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpEmailModal;
